/**
 * Get the current theme colors from CSS custom properties (no React hook).
 * For logo, use useTheme() from components.
 */
export const getThemeColors = () => {
  const primary = getCSSVariable("--primary-color") || "#24588C";
  const secondary = getCSSVariable("--secondary-color") || "#16A34A";
  return {
    primary,
    secondary,
    logo: "",
  };
};

/**
 * Get CSS custom property value
 */
export const getCSSVariable = (variableName: string): string => {
  return getComputedStyle(document.documentElement).getPropertyValue(variableName).trim();
};

/**
 * Parse hex color to r,g,b (0-255). Supports #RGB, #RRGGBB.
 */
function parseHex(hex: string): [number, number, number] | null {
  const cleaned = hex.replace(/^#/, "");
  if (cleaned.length === 3) {
    const r = parseInt(cleaned[0] + cleaned[0], 16);
    const g = parseInt(cleaned[1] + cleaned[1], 16);
    const b = parseInt(cleaned[2] + cleaned[2], 16);
    return [r, g, b];
  }
  if (cleaned.length === 6) {
    const r = parseInt(cleaned.slice(0, 2), 16);
    const g = parseInt(cleaned.slice(2, 4), 16);
    const b = parseInt(cleaned.slice(4, 6), 16);
    return [r, g, b];
  }
  return null;
}

/**
 * Returns black (#000000) or white (#ffffff) for readable text on the given background color.
 * Uses relative luminance; threshold 0.179 (WCAG) so light backgrounds get black text.
 */
export const getContrastTextColor = (bgColor: string): string => {
  const hex = bgColor.trim();
  const rgb = parseHex(hex);
  if (!rgb) return "#ffffff";
  const [r, g, b] = rgb.map((v) => v / 255);
  const toLinear = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  const L = 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
  return L > 0.179 ? "#000000" : "#ffffff";
};

/**
 * Apply theme colors to an element's style
 */
export const applyThemeColors = (element: HTMLElement) => {
  const primaryColor = getCSSVariable("--primary-color");
  const secondaryColor = getCSSVariable("--secondary-color");

  return {
    primary: primaryColor,
    secondary: secondaryColor,
  };
};
